import toast from 'react-hot-toast';

interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
      errors?: Array<{ msg?: string; message?: string }>;
      details?: Array<{ msg?: string; message?: string }>;
    };
    status?: number;
  };
  message?: string;
  code?: string;
}

/**
 * Enhanced error handler that provides specific error messages
 */
export class ErrorHandler {
  /**
   * Handle API errors with specific error messages
   */
  static handleApiError(error: ApiError, defaultMessage = 'An error occurred'): string {
    console.error('API Error:', error);

    // Handle different types of API errors
    if (error.response?.data) {
      const { data, status } = error.response;

      // Handle validation errors
      if (data.errors && Array.isArray(data.errors)) {
        const errorMessages = data.errors.map(err => err.msg || err.message).join(', ');
        return `Validation failed: ${errorMessages}`;
      }

      // Handle detailed errors
      if (data.details && Array.isArray(data.details)) {
        const errorMessages = data.details.map(err => err.msg || err.message).join(', ');
        return `Error details: ${errorMessages}`;
      }

      // Handle specific error messages
      if (data.message) {
        return data.message;
      }

      if (data.error) {
        return data.error;
      }

      // Handle HTTP status codes
      switch (status) {
        case 400:
          return 'Bad request - Please check your input data';
        case 401:
          return 'Authentication required - Please log in again';
        case 403:
          return 'Access denied - You do not have permission for this action';
        case 404:
          return 'Resource not found - The requested item does not exist';
        case 409:
          return 'Conflict - This item already exists or is in use';
        case 422:
          return 'Invalid data - Please check your input and try again';
        case 429:
          return 'Too many requests - Please wait a moment and try again';
        case 500:
          return 'Server error - Please try again later';
        case 502:
          return 'Bad gateway - Server is temporarily unavailable';
        case 503:
          return 'Service unavailable - Server is under maintenance';
        default:
          return `Server error (${status}) - Please try again later`;
      }
    }

    // Handle network errors
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return 'Network error - Please check your internet connection';
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout - Please try again';
    }

    // Handle other errors
    if (error.message) {
      return error.message;
    }

    return defaultMessage;
  }

  /**
   * Show error toast with specific message
   */
  static showError(error: ApiError, defaultMessage = 'An error occurred'): void {
    const errorMessage = this.handleApiError(error, defaultMessage);
    toast.error(errorMessage);
  }

  /**
   * Handle specific operation errors with context
   */
  static handleOperationError(operation: string, error: ApiError): void {
    const contextMessages: Record<string, string> = {
      'fetch': 'Failed to load data',
      'create': 'Failed to create item',
      'update': 'Failed to update item',
      'delete': 'Failed to delete item',
      'export': 'Failed to export data',
      'import': 'Failed to import data',
      'send': 'Failed to send',
      'generate': 'Failed to generate',
      'save': 'Failed to save',
      'load': 'Failed to load',
      'process': 'Failed to process',
      'validate': 'Validation failed',
      'authenticate': 'Authentication failed',
      'authorize': 'Authorization failed'
    };

    const defaultMessage = contextMessages[operation] || `Failed to ${operation}`;
    this.showError(error, defaultMessage);
  }

  /**
   * Handle validation errors specifically
   */
  static handleValidationError(error: ApiError): void {
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors.map((err: any) => err.msg || err.message).join(', ');
      toast.error(`Validation errors: ${errorMessages}`);
    } else if (error.response?.data?.message) {
      toast.error(`Validation failed: ${error.response.data.message}`);
    } else {
      toast.error('Please check your input data and try again');
    }
  }

  /**
   * Handle network errors specifically
   */
  static handleNetworkError(error: ApiError): void {
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      toast.error('Network error - Please check your internet connection and try again');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout - Please try again');
    } else {
      toast.error('Connection error - Please try again');
    }
  }

  /**
   * Handle authentication errors
   */
  static handleAuthError(error: ApiError): void {
    if (error.response?.status === 401) {
      toast.error('Your session has expired - Please log in again');
    } else if (error.response?.status === 403) {
      toast.error('Access denied - You do not have permission for this action');
    } else {
      toast.error('Authentication error - Please log in again');
    }
  }
}

/**
 * Utility function for common error handling patterns
 */
export const handleError = {
  /**
   * Generic error handler
   */
  generic: (error: ApiError, operation = 'operation') => {
    ErrorHandler.handleOperationError(operation, error);
  },

  /**
   * API call error handler
   */
  api: (error: ApiError, operation = 'API call') => {
    ErrorHandler.handleOperationError(operation, error);
  },

  /**
   * Validation error handler
   */
  validation: (error: ApiError) => {
    ErrorHandler.handleValidationError(error);
  },

  /**
   * Network error handler
   */
  network: (error: ApiError) => {
    ErrorHandler.handleNetworkError(error);
  },

  /**
   * Authentication error handler
   */
  auth: (error: ApiError) => {
    ErrorHandler.handleAuthError(error);
  }
};

export default ErrorHandler;
